import styled from 'styled-components';
import Plate, { Row, RowLabel, RowValue, Label, Divider, Dot } from './Plate';
import { COLORS, display, monoLabel } from '../../lib/theme';
import { formatAxisState, hasFlag, ODriveBrakeFlag } from '../../lib/bitmasks';
import { linkColor, LINK_LABEL } from '../../lib/link';

const Stack = styled(Plate)`
  top: 16px;
  right: 16px;
  width: 250px;
  max-width: 46%;
  background: ${COLORS.stackFill};

  @media (max-width: 900px) {
    position: static;
    width: 100%;
    max-width: none;
  }
`;

const Meter = styled.div`
  height: 3px;
  background: ${COLORS.hairline};
  margin-top: 5px;
`;

const MeterFill = styled.div`
  height: 100%;
  width: ${(p) => p.$pct}%;
  background: ${COLORS.live};
`;

const HeroLabel = styled(Label)`
  display: block;
  margin-bottom: 2px;
`;

const HeroValue = styled.div`
  ${display}
  font-size: 30px;
  line-height: 1.05;
  color: ${COLORS.accent};
  font-variant-numeric: tabular-nums;
`;

const HeroUnit = styled.span`
  ${monoLabel}
  font-size: 11px;
  color: ${COLORS.accent};
  margin-left: 4px;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 5px;
`;

const StatusText = styled.span`
  ${monoLabel}
  font-size: 9px;
  color: ${COLORS.text};
`;

const show = (field, format, fallback = '--') =>
  field?.everReceived && field.value != null ? format(field.value) : fallback;

/**
 * CAM 1's priority plate. Sections are divided by hairlines rather than boxed
 * separately, and only the INA production figure carries the yellow -- it's
 * the one number this panel exists to show.
 */
export default function TelemetryStack({ fields, linkState, rates }) {
  const soc = fields.soc;
  const socPct = soc.everReceived && soc.value != null ? Math.max(0, Math.min(100, soc.value)) : 0;
  const inaHz = rates?.ina;
  const showBrake =
    hasFlag(fields.brakeFlags.value, ODriveBrakeFlag.SATURATED) ||
    (fields.brakeDuty.value ?? 0) > 0;

  return (
    <Stack $priority>
      <Row>
        <RowLabel>BATTERY SOC</RowLabel>
        <RowValue>{show(soc, (v) => `${v.toFixed(0)} %`)}</RowValue>
      </Row>
      <Meter>
        <MeterFill $pct={socPct} />
      </Meter>
      <Row style={{ marginTop: 7 }}>
        <RowLabel>BATTERY V</RowLabel>
        <RowValue>{show(fields.packVoltage, (v) => `${v.toFixed(1)} V`)}</RowValue>
      </Row>

      <Divider />

      <HeroLabel>PWR production · INA</HeroLabel>
      <HeroValue>
        {show(fields.inaPower, (v) => v.toFixed(1))}
        <HeroUnit>W</HeroUnit>
      </HeroValue>

      <Divider />

      <Row>
        <RowLabel>STATE</RowLabel>
        <RowValue>{show(fields.machineState, (v) => v)}</RowValue>
      </Row>
      <Row>
        <RowLabel>ODRIVE</RowLabel>
        <RowValue>{show(fields.axisState, formatAxisState)}</RowValue>
      </Row>
      <Row>
        <RowLabel>MOTOR</RowLabel>
        <RowValue>{show(fields.motorRpm, (v) => `${v.toFixed(0)} RPM`)}</RowValue>
      </Row>
      {/* Only worth a line when the drive is actually dumping energy --
          otherwise it's a permanent 0% that crowds the stack. */}
      {showBrake && (
        <Row>
          <RowLabel>BRAKE R</RowLabel>
          <RowValue>{show(fields.brakeDuty, (v) => `${(v * 100).toFixed(0)} %`)}</RowValue>
        </Row>
      )}

      <Divider />

      <Row>
        <RowLabel>PRESSURE</RowLabel>
        <RowValue>{show(fields.pressure, (v) => `${v.toFixed(1)} hPa`)}</RowValue>
      </Row>
      <Row>
        <RowLabel>TEMP</RowLabel>
        <RowValue>{show(fields.temperature, (v) => `${v.toFixed(1)} °C`)}</RowValue>
      </Row>
      <Row>
        <RowLabel>HUMIDITY</RowLabel>
        <RowValue>{show(fields.humidity, (v) => `${v.toFixed(0)} %`)}</RowValue>
      </Row>

      <Divider />

      <StatusRow>
        <Dot $color={linkColor(linkState)} />
        <StatusText>{LINK_LABEL[linkState] || LINK_LABEL.nodata}</StatusText>
      </StatusRow>
      <Row>
        <RowLabel>DATA RATE</RowLabel>
        <RowValue>{inaHz != null ? `${inaHz.toFixed(1)} Hz` : '--'}</RowValue>
      </Row>
      <Row>
        <RowLabel>SIGNAL</RowLabel>
        <RowValue>{show(fields.signalPercent, (v) => `${v.toFixed(0)} %`)}</RowValue>
      </Row>
      <Row>
        <RowLabel>LINK</RowLabel>
        <RowValue>{show(fields.signalOperator, (v) => v)}</RowValue>
      </Row>

      <Divider />

      {/* The Pi's own health, from denshi-camera's status. It is the other
          thing in the unit that talks, and the fallback for LIVE above. */}
      <Row>
        <RowLabel>PI CPU</RowLabel>
        <RowValue>{show(fields.piCpuTemp, (v) => `${v.toFixed(0)} °C`)}</RowValue>
      </Row>
      <Row>
        <RowLabel>PI DISK</RowLabel>
        <RowValue>{show(fields.piDiskFree, (v) => `${(v / 1e9).toFixed(1)} GB`)}</RowValue>
      </Row>
    </Stack>
  );
}
